import { useEffect, useState } from 'react';
import type { PostResponsePayload } from 'constants/payloads/files/sign';
import { FileFormatEnum } from 'types/generated/client';
import { type ProfilePhotos, type UploadedImage, toUploadedImage } from 'utils/shared/playerFinder';
import { UploadProvider } from 'context/UploadVideoContext';
import { useUploadVideoFile } from 'hooks/useUploadVideoFile';
import FileUploader, { type SelectedFile } from 'components/VideoAnalysis/FileUploader';
import ProfileFormModal from './ProfileFormModal';
import type { ProfileEditorModalProps } from './types';

interface Props extends ProfileEditorModalProps {
  defaultPhotos: ProfilePhotos | null;
  onSave: (photos: UploadedImage[]) => Promise<boolean>;
}

function PhotosForm({ isOpen, onClose, defaultPhotos, onSave, isSaving, error }: Props) {
  // Seeded with the gallery already on the profile, so the modal shows what's
  // there. Rows carrying an id are persisted; the rest are this session's uploads.
  const [photos, setPhotos] = useState<UploadedImage[]>(defaultPhotos?.additionalPhotos ?? []);
  const { uploadVideoFile, uploadProgress } = useUploadVideoFile();
  const isUploading = uploadProgress !== null && uploadProgress < 100;

  useEffect(() => {
    if (isOpen) setPhotos(defaultPhotos?.additionalPhotos ?? []);
  }, [isOpen, defaultPhotos]);

  const handleSelect = async (files: File[]) => {
    if (!files.length) return;

    try {
      const payloads = await Promise.all(files.map((file) => uploadVideoFile({ file })));
      const uploaded = payloads.filter((payload): payload is PostResponsePayload => !!payload);
      setPhotos((current) => [...current, ...uploaded.map(toUploadedImage)]);
    } catch {
      // Surfaced by the upload hook's error handling; the player can retry.
    }
  };

  const selectedFiles: SelectedFile[] = photos.map((photo) => ({
    url: photo.url,
    fileFormat: photo.fileType.startsWith('video/') ? FileFormatEnum.Video : FileFormatEnum.Image,
    name: photo.originalFileName || photo.fileName,
  }));

  const handleSave = async () => {
    const saved = await onSave(photos);
    if (saved) onClose();
  };

  return (
    <ProfileFormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Photo gallery"
      description="Add photos to your gallery, or remove the ones you no longer want."
      onSave={handleSave}
      isSaving={isSaving || isUploading}
      error={error}
    >
      <FileUploader
        accept={{ 'image/png': [], 'image/jpeg': [] }}
        maxSize={100 * 1024 * 1024}
        styles="rounded-md"
        helperText="PNG or JPG (max. 100 MB)"
        uploadProgress={uploadProgress}
        selectedFiles={selectedFiles}
        onSelectFiles={(files) => void handleSelect(files)}
        onRemoveFile={(file) =>
          setPhotos((current) => current.filter((photo) => photo.url !== file.url))
        }
      />
    </ProfileFormModal>
  );
}

/** The upload hook reads its progress from context, so the form needs the provider. */
export default function PhotosFormModal(props: Props) {
  return (
    <UploadProvider>
      <PhotosForm {...props} />
    </UploadProvider>
  );
}
